import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import localeDa from "@angular/common/locales/da";
import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NavigationEnd, Router } from "@angular/router";

import { mdiClose } from "@mdi/js";
import "hammerjs"; // Kræves for at kunne dragge MatSlideToggle
import { CookieService } from "ngx-cookie-service";
import { filter } from "rxjs/operators";
import { first } from "rxjs/operators";

import { erstatEmailValidatorMedDffEdbUdgave } from "@dffedb/forms";
import { DffSimpelDialogModule, MatDffDateModule } from "@dffedb/ui";
import { ENV, LogentriesLoggerService, LOGGER, SvgIconRegistry } from "@dffedb/util";
import { AppShellModule } from "@e-forsyning/common/app-shell";
import { EBoksPopupService, EBoksTilmeldingModule } from "@e-forsyning/common/e-boks";
import { EforsyningMenuService, EforsyningSidenavMenuModule } from "@e-forsyning/common/eforsyning-menu";
import { EforsyningErrorHandler } from "@e-forsyning/common/error";
import { EforsyningHttpInterceptor } from "@e-forsyning/common/http";
import { KontaktoplysningerPopupModule, KontaktoplysningerPopupService } from "@e-forsyning/common/kontaktoplysninger-popup";
import { initUmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger";
import { RolleBodyClassService, RouteBodyClassService } from "@e-forsyning/common/util";
import { EForsyningFeaturesDefaultModule } from "@e-forsyning/features/default";
import { EForsyningFeaturesLoginModule } from "@e-forsyning/features/login";
import { DffedbCookieConsentModule, DffedbDriftsstatusKnapModule, DffedbFancyboxModule, EForsyningLogindKnapModule } from "@e-forsyning/web-components";

import { environment } from "../environments/environment";
import { AppInfoComponent } from "./app-info/app-info.component";
import { AppRoutingModule } from "./app-routing.module";
import { AppVaelgInstallationComponent } from "./app-vaelg-installation/app-vaelg-installation.component";
import { AppComponent } from "./app.component";
import { MatomoModule, MatomoRouterModule } from "ngx-matomo-client";
import { MatomoConfigService, matomoProviders } from "./matomo/matomo-config.service";
import { AktuelBrugersInstallationerService } from "@e-forsyning/common/installation/aktuel-brugers-installationer.service";

registerLocaleData(localeDa);
erstatEmailValidatorMedDffEdbUdgave();

@NgModule({
    declarations: [AppComponent, AppInfoComponent, AppVaelgInstallationComponent],
    imports: [
        AppShellModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        BrowserAnimationsModule,
        MatButtonModule,
        MatIconModule,
        MatSnackBarModule,
        MatDffDateModule,
        DffSimpelDialogModule,
        EForsyningFeaturesDefaultModule,
        EForsyningFeaturesLoginModule,
        EforsyningSidenavMenuModule,
        EBoksTilmeldingModule,
        KontaktoplysningerPopupModule,
        EForsyningLogindKnapModule,
        DffedbCookieConsentModule,
        DffedbDriftsstatusKnapModule,
        DffedbFancyboxModule,
        MatomoRouterModule,
        MatomoModule
    ],
    providers: [
        CookieService,
        {
            provide: LOCALE_ID,
            useValue: "da"
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: EforsyningHttpInterceptor,
            multi: true
        },
        {
            provide: ENV,
            useValue: environment
        },
        {
            provide: LOGGER,
            useClass: LogentriesLoggerService
        },
        {
            provide: ErrorHandler,
            useClass: EforsyningErrorHandler
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInit,
            deps: [Injector],
            multi: true
        },
        ...matomoProviders,
        AktuelBrugersInstallationerService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor(svgIconRegistry: SvgIconRegistry) {
        svgIconRegistry.registerIcon(mdiClose, "app-info-close", "dummy-to-trigger-build-v1");
    }
}

export function appInit(injector: Injector): () => void {
    return () => {
        initUmbracoIndstillinger(injector, (window as any).initUmbracoIndstillinger);
        initBodyClassServices(injector);
        initPopups(injector);
        injector.get(EforsyningMenuService).init();
    };
}

function initBodyClassServices(injector: Injector): void {
    injector.get(RouteBodyClassService).setup();
    injector.get(RolleBodyClassService).setup();
}

function initPopups(injector: Injector): void {
    const router = injector.get(Router);
    const eboksPopupService = injector.get(EBoksPopupService);
    const kontaktoplysningerPopupService = injector.get(KontaktoplysningerPopupService);
    const aktuelBrugersInstallationerService = injector.get(AktuelBrugersInstallationerService);

    router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(async () => {
        const installationer = await aktuelBrugersInstallationerService.select().pipe(first()).toPromise();
        if (installationer.length === 1) {
            eboksPopupService.aabnHvisIkkeAlleredeTilmeldt().then(() => {
                kontaktoplysningerPopupService.aabnHvisIkkeAlleredeTilmeldt();
            });
        }
    });
}
